import { useState } from 'react';
import { Box, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormAutocompleteMultiselectController,
  FormLanguageInputController,
  FormLanguageTextFieldController,
  FormSwitchController,
} from '@/components';
import { formatDecimalValueNumberInput, useGroups, useTags } from '@/core';
import { LanguageTypes, PriceWithDphCounter } from '@/features';
import palette from '@/theme/palette.ts';

import { styles } from './styles';

export const EditProductForm = () => {
  const { t } = useTranslation();
  const {
    useGroupsQuery: { data: groupsFromApi },
  } = useGroups({ page: 0, limit: 0 });
  const {
    useTagsQuery: { data: tagsFromApi },
  } = useTags({ page: 0, limit: 0 });
  const {
    formState: { errors },
  } = useFormContext();
  const [selectedNameLang, setSelectedNameLang] = useState<LanguageTypes>('cs');
  const [selectedDescriptionLang, setSelectedDescriptionLang] = useState<LanguageTypes>('cs');
  const { control } = useFormContext();
  return (
    <Box sx={styles.editProductFormContainer}>
      <Box mt={3}>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <Controller
              name="plu_code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="string"
                  value={field.value?.toString() || ''}
                  label={t('FINANCIAL_CLOSURE.PLU')}
                  error={!!errors.plu_code}
                  helperText={errors.plu_code?.message?.toString() || ' '}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="caption" minWidth="max-content">
                {t('GLOBAL.BASIC_INFORMATION')}
              </Typography>
              <Box
                sx={{
                  borderTop: `1px solid ${palette.surface.lighter}`,
                  height: '1px',
                  width: '100%',
                }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={10}>
            <FormLanguageInputController
              label={t('HEADERS.NAME')}
              controllerName="name"
              selectedLanguage={selectedNameLang}
              setSelectedLanguage={setSelectedNameLang}
              error={!!errors.name}
              errorText={t('GLOBAL.MIN_ONE_LANG')}
            />
          </Grid>

          <Grid item xs={12} md={2} alignSelf="center">
            <FormSwitchController controllerName="isActive" label={t('STATUS.ACTIVE')} />
          </Grid>

          <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
            <FormAutocompleteMultiselectController
              label={t('GLOBAL.SELECT_OFFER_CATEGORIES')}
              controllerName="groups"
              options={groupsFromApi?.results || []}
              autocompleteId="autocomplete-groups"
              placeholder={t('GLOBAL.OFFER_PLACEHOLDER')}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
            <FormAutocompleteMultiselectController
              label={t('GLOBAL.FILTER_BY_TAGS')}
              controllerName="tags"
              options={tagsFromApi?.results || []}
              autocompleteId="autocomplete-tags"
              placeholder={t('GLOBAL.TAG_PLACEHOLDER')}
            />
          </Grid>

          <Grid item xs={12}>
            <FormLanguageTextFieldController
              label={t('GLOBAL.DESCRIPTION_LABEL')}
              controllerName={'description'}
              helperText={t('GLOBAL.DESCRIPTION_HELPER')}
              selectedLanguage={selectedDescriptionLang}
              setSelectedLanguage={setSelectedDescriptionLang}
            />
          </Grid>
        </Grid>
      </Box>

      <Box mt={3}>
        <PriceWithDphCounter />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        mt={2}
        sx={{
          backgroundColor: palette.surface.lighter,
          p: '20px',
          borderRadius: '10px',
        }}
      >
        <Controller
          name="takeout_packaging_surcharge"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              value={field.value?.toString() || ''}
              onChange={(e) => {
                field.onChange(formatDecimalValueNumberInput(e.target.value));
              }}
              label={t('PRODUCT.PACKAGING_SURCHARGE')}
              sx={{ flex: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body1" color="text.secondary">
                      {t('GLOBAL.CZ_CURRENCY')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 2 }}>
          {t('PRODUCT.PACKAGING_SURCHARGE_HELPER')}
        </Typography>
      </Stack>
    </Box>
  );
};
